import { useTheme } from "@emotion/react";
import { Unstable_Grid2 as Grid2, Stack, Typography, Button, Skeleton, IconButton, Tooltip } from "@mui/material";
import React, { useState } from "react";
// import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, MoreVert, Refresh } from "@mui/icons-material";

const AutobiaStatsBox = ({ 
  title,
  value, 
  currency = "SAR",
  icon,
  trend = null,
  trendDirection = "up", // "up", "down", "neutral"
  isLoading = false,
  size = "medium", // "small", "medium", "large"
  variant = "default", // "default", "outlined", "contained"
  actionButton = null,
  onAction = () => {},
  onRefresh = null,
  showTrend = true,
  customColor = null,
  gradient = false,
  interactive = true,
  subtitle = null
}) => {
  //SECTION - general
  const theme = useTheme();
  const t = (key) => {
    if (key === "actions.refresh") return "Refresh";
    if (key.startsWith("currency.")) return key.replace("currency.", "");
    return key;
  };
  const [isHovered, setIsHovered] = useState(false);

  //SECTION - functions
  const getTrendColor = () => {
    switch (trendDirection) {
      case "up": return theme.palette.success?.main || "#4CAF50";
      case "down": return theme.palette.error?.main || "#F44336";
      default: return theme.palette.warning?.main || "#FF9800";
    }
  };

  const getTrendIcon = () => {
    switch (trendDirection) {
      case "up": return <TrendingUp fontSize="small" />;
      case "down": return <TrendingDown fontSize="small" />;
      default: return null;
    }
  };

  const getIconColor = () => {
    if (customColor) return customColor;
    switch (trendDirection) {
      case "up": return {
        bgColor: theme.palette.success?.light || "#E8F5E8",
        color: theme.palette.success?.main || "#4CAF50"
      };
      case "down": return {
        bgColor: theme.palette.error?.light || "#FFEBEE",
        color: theme.palette.error?.main || "#F44336"
      };
      default: return {
        bgColor: theme.palette.primary?.light || "#E3F2FD",
        color: theme.palette.primary?.main || "#2196F3"
      };
    }
  };

  const getSizeConfig = () => {
    switch (size) {
      case "small": return {
        iconSize: { width: "40px", height: "40px" },
        titleVariant: "body2",
        valueVariant: "h6",
        padding: "1rem"
      };
      case "large": return {
        iconSize: { width: "70px", height: "70px" },
        titleVariant: "h6",
        valueVariant: "h4",
        padding: "1.5rem"
      };
      default: return {
        iconSize: { width: "55px", height: "55px" },
        titleVariant: "body1",
        valueVariant: "h5",
        padding: "1.25rem"
      };
    }
  };

  const getVariantStyles = () => {
    const baseStyles = {
      borderRadius: "15px",
      height: "fit-content",
      cursor: interactive ? "pointer" : "default",
      position: "relative",
      overflow: "hidden",
    };

    if (gradient) {
      return {
        ...baseStyles,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
        border: `1px solid ${theme.palette.grey[200]}`,
        backgroundColor: "transparent",
      };
    }

    switch (variant) {
      case "outlined":
        return {
          ...baseStyles,
          border: `2px solid ${getIconColor().color}`,
          backgroundColor: "transparent",
        };
      case "contained":
        return {
          ...baseStyles,
          backgroundColor: getIconColor().bgColor,
          border: "none",
        };
      default:
        return {
          ...baseStyles,
          border: `1px solid ${theme.palette.grey[200]}`,
          backgroundColor: theme.palette.background.paper,
        };
    }
  };

  const { bgColor, color } = getIconColor();
  const sizeConfig = getSizeConfig();

  return (
    <Grid2
      direction={"row"}
      size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
      container
      alignItems={"center"}
      spacing={"0.5rem"}
      component={motion.div}
      whileHover={interactive ? { scale: 1.02, y: -2 } : {}}
      transition={{ duration: 0.2 }}
      sx={getVariantStyles()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isLoading ? (
        <Skeleton
          variant="rectangular"
          sx={{ borderRadius: "15px" }}
          width={"100%"}
          height={size === "large" ? 120 : size === "small" ? 80 : 100}
        />
      ) : (
        <Stack
          sx={{
            padding: sizeConfig.padding,
            width: "100%",
          }}
          direction={"column"}
          gap={"0.75rem"}
        >
          {/* Header with actions */}
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"}>
            <Stack direction={"row"} alignItems={"center"} gap={"0.75rem"}>
              {/* Icon */}
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                sx={{
                  ...sizeConfig.iconSize,
                  backgroundColor: bgColor,
                  color: color,
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  ...(isHovered && {
                    transform: "scale(1.1)",
                    boxShadow: `0 4px 20px ${color}40`,
                  })
                }}
                component={motion.div}
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.5 }}
              >
                {icon}
              </Stack>
              
              {/* Title and Subtitle */}
              <Stack direction={"column"} gap={"0.25rem"} flex={1}>
                <Typography 
                  variant={sizeConfig.titleVariant} 
                  color="text.secondary"
                  fontWeight={500}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography 
                    variant="caption" 
                    color="text.disabled"
                  >
                    {subtitle}
                  </Typography>
                )}
              </Stack>
            </Stack>

            {/* Action Buttons */}
            <Stack direction={"row"} gap={"0.25rem"}>
              {onRefresh && (
                <Tooltip title={t ? t("actions.refresh") : "Refresh"}>
                  <IconButton
                    size="small"
                    onClick={onRefresh}
                    sx={{
                      opacity: isHovered ? 1 : 0.5,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                sx={{
                  opacity: isHovered ? 1 : 0.3,
                  transition: "opacity 0.2s ease",
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {/* Value and Trend */}
          <Stack direction={"row"} alignItems={"baseline"} gap={"0.5rem"} justifyContent={"space-between"}>
            <NumericFormat
              value={value}
              dir="ltr"
              displayType="text"
              thousandSeparator=","
              renderText={(formattedValue) => (
                <Stack direction={"row"} alignItems={"baseline"} gap={"0.25rem"}>
                  <Typography 
                    variant={sizeConfig.valueVariant} 
                    fontWeight={700} 
                    color={variant === "contained" ? color : "text.primary"}
                    component={motion.span}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {formattedValue}
                  </Typography>
                  <Typography
                    component="span"
                    fontWeight={600}
                    variant="body2"
                    sx={{ 
                      marginLeft: "0.25rem",
                      color: variant === "contained" ? color : "text.secondary"
                    }}
                  >
                    {t ? t(`currency.${currency}`) : currency}
                  </Typography>
                </Stack>
              )}
            />
            
            {/* Trend Indicator */}
            {showTrend && trend !== null && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={"0.25rem"}
                sx={{
                  color: getTrendColor(),
                  backgroundColor: `${getTrendColor()}20`,
                  padding: "0.25rem 0.5rem",
                  borderRadius: "8px",
                }}
                component={motion.div}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {getTrendIcon()}
                <Typography variant="caption" fontWeight={600}>
                  {Math.abs(trend).toFixed(1)}%
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Action Button */}
          {actionButton && (
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              size={size === "small" ? "small" : "medium"}
              onClick={onAction}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                marginTop: "0.5rem",
              }}
              component={motion.div}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {actionButton}
            </Button>
          )}
        </Stack>
      )}
    </Grid2>
  );
};

export default AutobiaStatsBox;